import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ChartBar, ChartLine, ChartPieSlice, Table as TableIcon, DownloadSimple, Sparkle,
  Bookmarks, FloppyDisk, Trash, PaperPlaneTilt, Clock, Plus, Wrench, PencilSimple,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";
import { motion } from "framer-motion";

import FilterBar, { emptyFilters, filtersToParams } from "@/components/FilterBar";
import { useAuth } from "@/lib/auth";

const PIE_COLORS = ["#002FA7", "#059669", "#D97706", "#4F46E5", "#DC2626", "#0EA5E9", "#0F172A", "#7C3AED"];

export default function Reports() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const [options, setOptions] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [tab, setTab] = useState("standard");
  const [selectedKey, setSelectedKey] = useState("pipeline_summary");
  const [filters, setFilters] = useState(emptyFilters());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Custom builder
  const [customCfg, setCustomCfg] = useState({
    title: "Custom Report",
    group_by: "status",
    metric: "count",
    chart_type: "bar",
  });
  const [saveDialog, setSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveSchedule, setSaveSchedule] = useState(null);

  // Edit-schedule dialog for an existing saved report
  const [scheduleEditReport, setScheduleEditReport] = useState(null);
  const [scheduleDraft, setScheduleDraft] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/reports/options").then((r) => r.data),
      api.get("/reports/saved").then((r) => r.data).catch(() => []),
    ]).then(([opts, saved]) => {
      setOptions(opts);
      setSavedReports(saved);
    }).catch(() => toast.error("Failed to load report options"));
  }, []);

  const runStandard = async (key, withFilters = filters) => {
    setLoading(true);
    setSelectedKey(key);
    try {
      const { data } = await api.get(`/reports/standard/${key}`, { params: filtersToParams(withFilters) });
      setReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report failed");
    } finally {
      setLoading(false);
    }
  };

  const runCustom = async (withFilters = filters) => {
    setLoading(true);
    try {
      const params = filtersToParams(withFilters);
      const { data } = await api.post("/reports/custom", {
        ...customCfg,
        ...params,
        statuses: withFilters.statuses,
        sources: withFilters.sources,
        departments: withFilters.departments,
        assignees: withFilters.assignees,
        min_days_to_close: withFilters.min_days_to_close === "" ? null : withFilters.min_days_to_close,
        max_days_to_close: withFilters.max_days_to_close === "" ? null : withFilters.max_days_to_close,
      });
      setReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report failed");
    } finally {
      setLoading(false);
    }
  };

  const onApplyFilters = (next) => {
    setFilters(next);
    if (tab === "standard" && report) runStandard(selectedKey, next);
    else if (tab === "custom" && report) runCustom(next);
  };

  const downloadCsv = async () => {
    try {
      let resp;
      if (tab === "standard") {
        resp = await api.get(`/reports/standard/${selectedKey}/export.csv`, {
          params: filtersToParams(filters),
          responseType: "blob",
        });
      } else {
        resp = await api.post("/reports/custom/export.csv", {
          ...customCfg,
          ...filtersToParams(filters),
          statuses: filters.statuses,
          sources: filters.sources,
          departments: filters.departments,
          assignees: filters.assignees,
          min_days_to_close: filters.min_days_to_close === "" ? null : filters.min_days_to_close,
          max_days_to_close: filters.max_days_to_close === "" ? null : filters.max_days_to_close,
        }, { responseType: "blob" });
      }
      const fname = (report?.title || "report").replace(/\s+/g, "-") + ".csv";
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("CSV download failed");
    }
  };

  const printPdf = () => {
    // Browser print — uses the on-screen report layout. Simpler than server-side rendering.
    window.print();
  };

  const saveCurrent = async () => {
    if (!saveName.trim()) { toast.error("Give the report a name"); return; }
    try {
      const params = filtersToParams(filters);
      const cfg = tab === "standard"
        ? { standard_key: selectedKey, ...params,
            statuses: filters.statuses, sources: filters.sources,
            departments: filters.departments, assignees: filters.assignees,
            min_days_to_close: filters.min_days_to_close === "" ? null : filters.min_days_to_close,
            max_days_to_close: filters.max_days_to_close === "" ? null : filters.max_days_to_close }
        : { ...customCfg, ...params,
            statuses: filters.statuses, sources: filters.sources,
            departments: filters.departments, assignees: filters.assignees,
            min_days_to_close: filters.min_days_to_close === "" ? null : filters.min_days_to_close,
            max_days_to_close: filters.max_days_to_close === "" ? null : filters.max_days_to_close };
      await api.post("/reports/saved", {
        name: saveName.trim(),
        config: cfg,
        schedule: saveSchedule,
      });
      toast.success("Report saved");
      setSaveDialog(false);
      setSaveName(""); setSaveSchedule(null);
      const list = await api.get("/reports/saved").then((r) => r.data);
      setSavedReports(list);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const loadSaved = async (r) => {
    const cfg = r.config || {};
    // Compute rolling window on the fly so presets stay evergreen.
    let df = cfg.date_from || "";
    if (!df && cfg.rolling_days) {
      const d = new Date();
      d.setDate(d.getDate() - Number(cfg.rolling_days));
      df = d.toISOString().slice(0, 10);
    }
    const nextFilters = {
      ...emptyFilters(),
      date_from: df,
      date_to: cfg.date_to || "",
      statuses: cfg.statuses || [],
      sources: cfg.sources || [],
      departments: cfg.departments || [],
      assignees: cfg.assignees || [],
      job_titles: cfg.job_titles || [],
      min_days_to_close: cfg.min_days_to_close ?? "",
      max_days_to_close: cfg.max_days_to_close ?? "",
    };
    setFilters(nextFilters);
    if (cfg.standard_key) {
      setTab("standard");
      setSelectedKey(cfg.standard_key);
      await runStandard(cfg.standard_key, nextFilters);
    } else {
      setTab("custom");
      setCustomCfg({
        title: r.name,
        group_by: cfg.group_by || "status",
        metric: cfg.metric || "count",
        chart_type: cfg.chart_type || "bar",
      });
      await runCustom(nextFilters);
    }
  };

  const deleteSaved = async (id) => {
    if (!window.confirm("Delete this saved report?")) return;
    try {
      await api.delete(`/reports/saved/${id}`);
      const list = await api.get("/reports/saved").then((r) => r.data);
      setSavedReports(list);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const openScheduleEditor = (r) => {
    setScheduleEditReport(r);
    setScheduleDraft(r.schedule ? { ...r.schedule } : null);
  };

  const saveScheduleEdit = async () => {
    if (!scheduleEditReport) return;
    setScheduleSaving(true);
    try {
      await api.patch(`/reports/saved/${scheduleEditReport.id}`, { schedule: scheduleDraft });
      const list = await api.get("/reports/saved").then((r) => r.data);
      setSavedReports(list);
      toast.success(scheduleDraft ? "Schedule updated" : "Schedule removed");
      setScheduleEditReport(null);
      setScheduleDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setScheduleSaving(false);
    }
  };

  const sendTestNow = async (r) => {
    setTestingId(r.id);
    try {
      const { data } = await api.post(`/reports/saved/${r.id}/test`);
      const to = (data?.recipients || []).join(", ");
      toast.success(`Test sent to ${to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setTestingId(null);
    }
  };

  if (!options) return <div data-testid="reports-loading" className="text-sm text-slate-500">Loading…</div>;

  return (
    <div data-testid="reports-page" className="space-y-8">
      <div>
        <div className="label-tracking inline-flex items-center gap-2"><Sparkle size={11} weight="fill" className="text-[#002FA7]" />Insight</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none mt-2 text-slate-900">Reports.</h1>
        <p className="text-sm text-slate-500 mt-3 max-w-2xl">
          Standard reports give you the headline view. Build your own with the visual builder, save it,
          and have a fresh CSV emailed weekly or monthly.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="reports-tablist" className="bg-transparent border-b border-slate-200 rounded-none h-auto p-0 gap-6 justify-start flex-wrap w-full">
          <TabsTrigger value="standard" data-testid="tab-standard" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none px-0 pb-3 gap-2 label-tracking !text-xs">
            <ChartBar size={13} /> Standard
          </TabsTrigger>
          <TabsTrigger value="custom" data-testid="tab-custom" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none px-0 pb-3 gap-2 label-tracking !text-xs">
            <Wrench size={13} /> Visual builder
          </TabsTrigger>
          <TabsTrigger value="saved" data-testid="tab-saved" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none px-0 pb-3 gap-2 label-tracking !text-xs">
            <Bookmarks size={13} /> Saved ({savedReports.length})
          </TabsTrigger>
        </TabsList>

        {/* Filters shared by tabs */}
        <div className="mt-6 space-y-3">
          <FilterBar
            value={filters}
            onApply={onApplyFilters}
            options={options}
            showJobTitles
          />
          <div className="flex items-end gap-2 flex-wrap">
            {tab === "standard" && (
              <Button data-testid="run-standard-btn" onClick={() => runStandard(selectedKey)} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2">
                Run report
              </Button>
            )}
            {tab === "custom" && (
              <Button data-testid="run-custom-btn" onClick={() => runCustom()} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2">
                Build report
              </Button>
            )}
            {report && (
              <>
                <Button data-testid="download-csv-btn" variant="outline" onClick={downloadCsv} className="rounded-sm gap-2">
                  <DownloadSimple size={14} /> CSV
                </Button>
                <Button data-testid="print-pdf-btn" variant="outline" onClick={printPdf} className="rounded-sm gap-2">
                  <DownloadSimple size={14} /> PDF
                </Button>
                <Button data-testid="save-report-btn" variant="outline" onClick={() => setSaveDialog(true)} className="rounded-sm gap-2">
                  <FloppyDisk size={14} /> Save
                </Button>
              </>
            )}
            {!report && (
              <Button variant="outline" disabled className="rounded-sm gap-2 opacity-50 cursor-not-allowed">
                <FloppyDisk size={14} /> Save
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="standard" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {options.standard_reports.map((r) => (
              <button
                key={r.key}
                data-testid={`std-${r.key}`}
                onClick={() => runStandard(r.key)}
                className={`text-left bg-white border rounded-sm p-4 transition-colors ${selectedKey === r.key ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-400"}`}
              >
                <div className="font-display text-lg text-slate-900">{r.title}</div>
                <div className="text-xs text-slate-500 mt-1">{r.description}</div>
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="custom" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-sm p-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <Label className="label-tracking">Title</Label>
              <Input data-testid="custom-title-input" value={customCfg.title} onChange={(e) => setCustomCfg({ ...customCfg, title: e.target.value })} className="mt-2 rounded-sm h-10" />
            </div>
            <div>
              <Label className="label-tracking">Group by</Label>
              <Select value={customCfg.group_by} onValueChange={(v) => setCustomCfg({ ...customCfg, group_by: v })}>
                <SelectTrigger data-testid="group-by-select" className="mt-2 rounded-sm h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.groupable_fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tracking">Metric</Label>
              <Select value={customCfg.metric} onValueChange={(v) => setCustomCfg({ ...customCfg, metric: v })}>
                <SelectTrigger data-testid="metric-select" className="mt-2 rounded-sm h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.metrics.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tracking">Chart type</Label>
              <Select value={customCfg.chart_type} onValueChange={(v) => setCustomCfg({ ...customCfg, chart_type: v })}>
                <SelectTrigger data-testid="chart-type-select" className="mt-2 rounded-sm h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {options.chart_types.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="saved" className="mt-6">
          {savedReports.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm p-10 text-center" data-testid="saved-empty">
              <Bookmarks size={32} className="text-slate-300 mx-auto" weight="duotone" />
              <p className="text-sm text-slate-500 mt-3">No saved reports yet. Build one in the Visual builder and click <span className="font-medium">Save</span>.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {savedReports.map((r) => (
                <div key={r.id} data-testid={`saved-${r.id}`} className="bg-white border border-slate-200 rounded-sm p-4 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <button onClick={() => loadSaved(r)} className="text-left block w-full">
                      <div className="font-display text-base text-slate-900 truncate">{r.name}</div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        {r.config?.standard_key ? "Standard report" : `Custom · group by ${r.config?.group_by || "—"} · ${r.config?.metric || "—"}`}
                      </div>
                      {r.schedule ? (
                        <div className="text-[11px] text-emerald-700 inline-flex items-center gap-1 mt-1">
                          <Clock size={10} /> {r.schedule.frequency || "weekly"} @ {r.schedule.hour || 8}h
                          {(r.schedule.recipients?.length || 0) > 0 && (
                            <span className="text-slate-500"> · {r.schedule.recipients.length} recipient{r.schedule.recipients.length === 1 ? "" : "s"}</span>
                          )}
                          {r.last_sent_at && <span className="text-slate-400" title={r.last_sent_at}> · sent {new Date(r.last_sent_at).toLocaleDateString()}</span>}
                        </div>
                      ) : (
                        <div className="text-[11px] text-slate-400 mt-1">No schedule</div>
                      )}
                    </button>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isAdmin && (
                      <>
                        <button
                          data-testid={`schedule-saved-${r.id}`}
                          title="Edit schedule"
                          onClick={() => openScheduleEditor(r)}
                          className="text-slate-400 hover:text-slate-900 p-1"
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          data-testid={`test-saved-${r.id}`}
                          title="Send test email now"
                          disabled={testingId === r.id}
                          onClick={() => sendTestNow(r)}
                          className="text-slate-400 hover:text-emerald-700 p-1 disabled:opacity-40"
                        >
                          <PaperPlaneTilt size={14} />
                        </button>
                      </>
                    )}
                    <button data-testid={`delete-saved-${r.id}`} onClick={() => deleteSaved(r.id)} className="text-slate-400 hover:text-rose-600 p-1">
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Report output */}
      {loading && <div className="text-sm text-slate-500" data-testid="report-loading">Running…</div>}
      {report && !loading && (
        <ReportView report={report} />
      )}

      <Dialog open={saveDialog} onOpenChange={setSaveDialog}>
        <DialogContent data-testid="save-report-dialog" className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Save report</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Give your report a name. Optionally, schedule a recurring email delivery (CSV attachment).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="label-tracking">Name</Label>
              <Input
                data-testid="save-name-input"
                autoFocus
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder={`e.g. ${report?.title || "Weekly pipeline"}`}
                className="mt-2 h-10 rounded-sm"
              />
            </div>
            <ScheduleEditor schedule={saveSchedule} onChange={setSaveSchedule} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setSaveDialog(false)}>Cancel</Button>
            <Button data-testid="save-confirm-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2" onClick={saveCurrent}>
              <FloppyDisk size={14} /> Save report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!scheduleEditReport} onOpenChange={(o) => { if (!o) { setScheduleEditReport(null); setScheduleDraft(null); } }}>
        <DialogContent data-testid="edit-schedule-dialog" className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Schedule</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {scheduleEditReport?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <ScheduleEditor schedule={scheduleDraft} onChange={setScheduleDraft} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setScheduleEditReport(null); setScheduleDraft(null); }}>Cancel</Button>
            <Button
              data-testid="schedule-save-btn"
              disabled={scheduleSaving}
              className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2"
              onClick={saveScheduleEdit}
            >
              <FloppyDisk size={14} /> {scheduleSaving ? "Saving…" : "Save schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScheduleEditor({ schedule, onChange }) {
  const enabled = !!schedule;
  const cur = schedule || { frequency: "weekly", day: 0, hour: 8, recipients: [] };
  const [recipientDraft, setRecipientDraft] = useState("");

  const setField = (k, v) => onChange({ ...cur, [k]: v });

  return (
    <div className="border border-slate-200 rounded-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-tracking">Email schedule</div>
          <p className="text-[11px] text-slate-500 mt-1">Send this report as a CSV attachment on a recurring basis.</p>
        </div>
        <button
          type="button"
          data-testid="schedule-toggle"
          onClick={() => onChange(enabled ? null : { frequency: "weekly", day: 0, hour: 8, recipients: [] })}
          className={`px-3 py-1 rounded-sm text-xs ${enabled ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}
        >
          {enabled ? "Enabled" : "Off"}
        </button>
      </div>
      {enabled && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="label-tracking">Frequency</Label>
            <Select value={cur.frequency} onValueChange={(v) => setField("frequency", v)}>
              <SelectTrigger className="mt-1 rounded-sm h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tracking">{cur.frequency === "weekly" ? "Day of week" : cur.frequency === "monthly" ? "Day of month" : "—"}</Label>
            <Input
              type="number"
              min={cur.frequency === "weekly" ? 0 : 1}
              max={cur.frequency === "weekly" ? 6 : 28}
              value={cur.day}
              onChange={(e) => setField("day", Number(e.target.value))}
              disabled={cur.frequency === "daily"}
              className="mt-1 h-9 rounded-sm text-xs"
            />
          </div>
          <div>
            <Label className="label-tracking">Hour (UTC)</Label>
            <Input type="number" min="0" max="23" value={cur.hour} onChange={(e) => setField("hour", Number(e.target.value))} className="mt-1 h-9 rounded-sm text-xs" />
          </div>
          <div className="col-span-3">
            <Label className="label-tracking">Recipients ({(cur.recipients || []).length})</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {(cur.recipients || []).map((r) => (
                <span key={r} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-slate-100 text-xs font-mono">
                  {r}
                  <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => setField("recipients", cur.recipients.filter((x) => x !== r))}>×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                type="email"
                placeholder="add@email"
                value={recipientDraft}
                onChange={(e) => setRecipientDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && recipientDraft.trim()) {
                    e.preventDefault();
                    setField("recipients", [...(cur.recipients || []), recipientDraft.trim()]);
                    setRecipientDraft("");
                  }
                }}
                className="h-9 rounded-sm text-xs font-mono flex-1"
              />
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm h-9"
                onClick={() => {
                  if (!recipientDraft.trim()) return;
                  setField("recipients", [...(cur.recipients || []), recipientDraft.trim()]);
                  setRecipientDraft("");
                }}
              >
                <Plus size={12} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportView({ report }) {
  const Chart = useMemo(() => () => renderChart(report.chart), [report]);
  return (
    <motion.div
      key={report.title}
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="report-view"
      id="report-view"
      className="space-y-6 bg-white border border-slate-200 rounded-sm p-6 print:border-0 print:p-0"
    >
      <div>
        <div className="label-tracking">{report.key ? "Standard" : "Custom"}</div>
        <h2 className="font-display text-3xl text-slate-900 mt-1">{report.title}</h2>
      </div>
      {report.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {report.summary.map((s, i) => (
            <div key={i} data-testid={`summary-${i}`} className="border border-slate-200 rounded-sm p-3">
              <div className="label-tracking">{s.label}</div>
              <div className={`mt-1 font-display text-2xl ${s.tone === "good" ? "text-emerald-700" : s.tone === "bad" ? "text-rose-700" : "text-slate-900"}`}>
                {s.value}
              </div>
            </div>
          ))}
        </div>
      )}
      {report.chart && report.chart.data && report.chart.data.length > 0 && (
        <div className="h-72" data-testid="report-chart">
          <ResponsiveContainer width="100%" height="100%">
            {Chart()}
          </ResponsiveContainer>
        </div>
      )}
      {report.table && (
        <div className="overflow-x-auto" data-testid="report-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                {report.table.columns.map((c, i) => (
                  <th key={i} className="text-left p-3 label-tracking !text-[0.65rem]">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.table.rows.map((row, i) => (
                <tr key={i} className="border-t border-slate-100">
                  {row.map((cell, j) => (
                    <td key={j} className="p-3 text-slate-700">{String(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}

function renderChart(chart) {
  if (!chart || !chart.data || chart.data.length === 0) return null;
  const t = chart.type;
  if (t === "pie") {
    return (
      <PieChart>
        <Pie dataKey={chart.value_key || "metric"} nameKey={chart.name_key || "group"} data={chart.data} cx="50%" cy="50%" outerRadius={90} label>
          {chart.data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    );
  }
  if (t === "line") {
    return (
      <LineChart data={chart.data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
        <XAxis dataKey={chart.x_key || "group"} fontSize={11} />
        <YAxis fontSize={11} />
        <Tooltip />
        <Line type="monotone" dataKey={chart.y_key || "metric"} stroke={chart.fill || "#002FA7"} strokeWidth={2} />
      </LineChart>
    );
  }
  // bar (default)
  return (
    <BarChart data={chart.data}>
      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
      <XAxis dataKey={chart.x_key || "group"} fontSize={11} />
      <YAxis fontSize={11} />
      <Tooltip />
      <Bar dataKey={chart.y_key || "metric"} fill={chart.fill || "#002FA7"} radius={[2, 2, 0, 0]} />
    </BarChart>
  );
}
